import { FloraVersion } from '../../containers/flora/flora.type';
import type { FloraContextualInfo } from '../../containers/flora/flora.type';
import type { UseQueryResult } from '@tanstack/react-query';

/**
 * Wire contract for the `askAgent` WebSocket message. `message` and `thread_id`
 * are always sent; every other field is included only when set. `document_ids`
 * carries the ids of user-uploaded Word/Excel files attached to the message so
 * the backend can persist them and surface them to Flora. Kept here as the
 * single source of truth for the payload shape so the sender cannot drift from
 * the backend contract.
 */
type AskAgentRequestPayload = {
  message: string;
  thread_id: string;
  portfolio_id?: string;
  contextual_info?: FloraContextualInfo;
  enable_artifacts?: boolean;
  model?: string;
  effort?: string;
  is_sidebar?: boolean;
  document_ids?: string[];
  approval_mode?: ApprovalMode;
  /** The chat's own composer choice, saved on the chat; absent when it follows the saved default. */
  chat_approval_mode?: ApprovalMode;
};

enum FloraIntegrationSource {
  Slack = 'slack',
  MicrosoftTeams = 'microsoft_teams',
  Webex = 'webex',
}

/**
 * A single Flora model option offered to super-users for model selection.
 */
type FloraModelOption = {
  model_id: string;
  display_name: string;
  provider: string;
  valid_efforts: string[];
  default_effort: string | null;
  is_default: boolean;
};

type UseAIResponse = {
  can_use_ai: boolean;
  flora_integration_statuses?: Record<FloraIntegrationSource, boolean>;
  flora_versions_available?: FloraVersion[];
  flora_models_available?: FloraModelOption[];
};

type SuggestedPromptsResponse = {
  suggested_prompts: string[];
};

/**
 * Document created by Flora document tools
 */
type ChatDocument = {
  id: string;
  filename: string;
  doc_type: 'pptx' | 'xlsx' | 'docx' | 'pdf' | 'csv' | 'tsv' | 'txt' | 'md' | 'json';
  file_size: number | null;
  created_at: string;
  download_url?: string;
  s3_key: string;
  chat_history_id: string | null;
  user: { id: string; name: string };
  /** Current revision number (from the list endpoint); absent on older payloads. */
  revision?: number;
  /** Origin of the document, e.g. `flora_generated` | `uploaded` (list endpoint). */
  source?: string;
};

/**
 * Response from `POST documents/upload/` for a user-uploaded read-only
 * Word/Excel file. A subset of `ChatDocument` — the upload endpoint returns
 * only these fields.
 */
type UploadedChatDocument = {
  id: string;
  filename: string;
  doc_type: ChatDocument['doc_type'];
  source: string;
  revision: number;
  file_size: number | null;
};

/**
 * One entry in a document's revision history, from
 * `GET documents/{id}/revisions/`. `download_url` is a short-lived presigned
 * url for that specific revision's stored file.
 */
type DocumentRevision = {
  revision: number;
  created_at: string;
  file_size: number | null;
  is_current: boolean;
  download_url?: string;
};

/**
 * Conversion status for a document's PDF preview:
 * - `ready` — the preview PDF exists; `preview_url` is a short-lived presigned URL.
 * - `processing` — a background conversion is running; poll again shortly.
 * - `error` — the conversion failed.
 */
type DocumentPreviewStatus = 'ready' | 'processing' | 'error';

/**
 * Response from `GET documents/{id}/preview/`. `preview_url` is present only
 * when `status` is `ready` and expires (presigned), so callers must re-fetch
 * before use rather than caching it.
 */
type DocumentPreviewResponse = {
  status: DocumentPreviewStatus;
  preview_url?: string;
};

/**
 * Response from `GET documents/{id}/content/` for a CSV, TSV, Markdown, JSON,
 * or plain-text document. `content` is capped server-side; `truncated` says
 * whether the file was longer than what was returned.
 */
interface DocumentContentResponse {
  document_id: string;
  filename: string;
  doc_type: 'csv' | 'tsv' | 'md' | 'json' | 'txt';
  revision: number;
  content: string;
  truncated: boolean;
}

type ImportantObject = {
  id: string;
  name: string;
  object_type: string;
};

/**
 * Tool-loading strategy Flora uses for a chat session. `OnDemand` searches for
 * and loads tools lazily (lean context); `All` loads every enabled tool upfront.
 */
enum ToolLoadingMode {
  OnDemand = 'ondemand',
  All = 'all',
}

/**
 * How Flora handles write tools that require approval. `Ask` raises an approval
 * card before each such write; `Auto` has Jev (TypeSafe) risk-check each call,
 * running low-risk calls and blocking risky ones.
 */
enum ApprovalMode {
  Ask = 'ask',
  Auto = 'auto',
}

/**
 * How a capability section is controlled in the Customize Flora page.
 * `Enable` sections toggle whole tools on/off; `Approval` sections keep tools
 * always available but toggle whether Flora needs human approval to run writes.
 */
enum CapabilityMode {
  Enable = 'enable',
  Approval = 'approval',
}

/**
 * Discriminator for a capability section: a `Bucket` groups several
 * capabilities under one connected account (e.g. Google), while a standalone
 * `Capability` is a single controllable capability (e.g. Slash commands).
 */
enum CapabilitySectionKind {
  Bucket = 'bucket',
  Capability = 'capability',
}

type FloraUserPreferences = {
  preferred_name?: string | null;
  role?: string | null;
  general_preferences?: string | null;
  important_objects?: ImportantObject[] | null;
  learned_preferences?: string[] | null;
  /** Tool-loading strategy for new chats. Absent ⇒ `ondemand`. */
  tool_loading?: ToolLoadingMode | null;
  /** Default approval mode for approval-required writes. Absent ⇒ `ask`. */
  default_approval_mode?: ApprovalMode | null;
  /** Normalized tool NAMES the user turned OFF. Absent ⇒ nothing disabled. */
  disabled_tools?: string[] | null;
  /** Map of approval-mode write tool name → approval_required. Absent ⇒ catalog defaults. */
  tool_approvals?: Record<string, boolean> | null;
  /** Whether Flora learns from and references this user's past chats in the org. Absent ⇒ on. */
  reference_chat_history?: boolean | null;
};

type FloraUserPreferencesResponse = {
  preferences: FloraUserPreferences;
};

/**
 * A single tool entry inside a capability's read or write group.
 *
 * `required` tools are always-on (no toggle). Enable-mode tools carry the
 * current `enabled` state; approval-mode write tools carry the current
 * `approval_required` state. The catalog is authoritative — these fields
 * already reflect the user's saved settings merged over defaults.
 */
interface CapabilityGroupTool {
  name: string;
  label: string;
  description: string;
  required: boolean;
  enabled?: boolean;
  approval_required?: boolean;
  /** Default approval posture for approval-mode write tools (used for "Reset to defaults"). */
  approval_default?: boolean;
}

/**
 * A single controllable capability (e.g. Docs) with its read and write tool
 * groups. Used both as a bucket child and as the body of a standalone section.
 */
interface CapabilityGroup {
  id: string;
  name: string;
  read: CapabilityGroupTool[];
  write: CapabilityGroupTool[];
  /**
   * Sibling capability ids this capability stays locked on with. While any tool
   * in one of those siblings is enabled, this capability's tools cannot be
   * disabled (its toggle is hidden); they only become disable-able once every
   * listed sibling is fully off. E.g. Office "General" is enabled_with excel + word.
   */
  enabled_with?: string[];
}

/**
 * A capability section grouping several capabilities under one connected
 * account (e.g. Google → Docs, Slides, Sheets, Drive).
 */
interface CapabilityBucket {
  kind: CapabilitySectionKind.Bucket;
  id: string;
  name: string;
  mode: CapabilityMode;
  /** Whether the underlying account is connected. When false the UI shows a Connect CTA instead of toggles. */
  connected: boolean;
  /** Connected account label (e.g. an email), or null when not connected. */
  account: string | null;
  capabilities: CapabilityGroup[];
}

/**
 * A standalone capability section (e.g. Slash commands, Automations, Targets)
 * whose read/write groups sit directly on the section.
 */
interface StandaloneCapability {
  kind: CapabilitySectionKind.Capability;
  id: string;
  name: string;
  mode: CapabilityMode;
  read: CapabilityGroupTool[];
  write: CapabilityGroupTool[];
}

type CapabilitySection = CapabilityBucket | StandaloneCapability;

/**
 * Response from the flora-capabilities endpoint: the available capability
 * catalog for the current user + organization, plus the current tool-loading
 * mode. Only AVAILABLE sections are returned (e.g. the Google bucket is
 * omitted entirely when Google isn't connected).
 */
interface FloraCapabilitiesResponse {
  tool_loading: ToolLoadingMode;
  /** The user's saved default approval mode (absent on older backends ⇒ `ask`). */
  default_approval_mode?: ApprovalMode;
  /** The user's saved "Reference chat history" setting (absent on older backends ⇒ on). */
  reference_chat_history?: boolean;
  /** Whether the organization has learned chat memory, i.e. whether the toggle applies. */
  reference_chat_history_available?: boolean;
  sections: CapabilitySection[];
}

type UseFloraCapabilitiesResult = {
  capabilities: FloraCapabilitiesResponse | undefined;
  isLoading: boolean;
  error: UseQueryResult<FloraCapabilitiesResponse, Error>['error'];
};

type MemoryType = 'workflow' | 'fact';

/**
 * A long-term memory the agent has saved for the user (org-scoped).
 */
type FloraMemory = {
  id: string;
  title: string;
  content: string;
  memory_type: MemoryType;
  trigger: string;
  source: 'agent_inferred' | 'user_explicit';
  status: 'active' | 'archived';
  created_at: string;
  updated_at: string;
};

type FloraMemoriesResponse = {
  memories: FloraMemory[];
};

/**
 * Slash command model from the backend
 */
type SlashCommand = {
  id: string;
  organization: string | null;
  user: string | null;
  name: string;
  description: string | null;
  prompt: string | null;
  metadata_created_at: string;
};

/**
 * Request body for creating or updating a slash command
 */
type SlashCommandWrite = {
  organization?: string | null;
  name: string;
  description?: string | null;
  prompt?: string | null;
};

type UseSuggestedPromptsResult = {
  data: SuggestedPromptsResponse | undefined;
  query: UseQueryResult<SuggestedPromptsResponse, Error>;
};

type UseDocumentPreviewResult = {
  preview: DocumentPreviewResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  /** True once polling gave up on a conversion that never settled. */
  timedOut: boolean;
  /** Clears the give-up state and re-polls from scratch. */
  retry: () => void;
};

type UseOrganizationDocumentsResult = {
  documents: ChatDocument[];
  isLoading: boolean;
  error: UseQueryResult<ChatDocument[], Error>['error'];
  refetch: UseQueryResult<ChatDocument[], Error>['refetch'];
};

type UseFloraUserPreferencesResult = {
  preferences: FloraUserPreferences | undefined;
  isLoading: boolean;
  error: UseQueryResult<FloraUserPreferencesResponse, Error>['error'];
};

type UseFloraMemoriesResult = {
  memories: FloraMemory[];
  isLoading: boolean;
  error: UseQueryResult<FloraMemoriesResponse, Error>['error'];
};

export { ApprovalMode, CapabilityMode, CapabilitySectionKind, FloraIntegrationSource, ToolLoadingMode };
export type {
  AskAgentRequestPayload,
  CapabilityBucket,
  CapabilityGroup,
  CapabilityGroupTool,
  CapabilitySection,
  ChatDocument,
  DocumentContentResponse,
  DocumentPreviewResponse,
  DocumentPreviewStatus,
  DocumentRevision,
  FloraCapabilitiesResponse,
  FloraModelOption,
  FloraMemory,
  FloraMemoriesResponse,
  FloraUserPreferences,
  FloraUserPreferencesResponse,
  ImportantObject,
  MemoryType,
  SlashCommand,
  SlashCommandWrite,
  StandaloneCapability,
  SuggestedPromptsResponse,
  UseFloraCapabilitiesResult,
  UseFloraMemoriesResult,
  UseFloraUserPreferencesResult,
  UseDocumentPreviewResult,
  UseOrganizationDocumentsResult,
  UseSuggestedPromptsResult,
  UploadedChatDocument,
  UseAIResponse,
};
